'use client';

import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, RotateCcw, Download } from 'lucide-react';

type Day = {
  id: string;
  label: string;
  remaining: number;
};

const STORAGE_KEY = 'planetroadmap.burndown-chart-generator.v1';

function newDay(label: string, remaining: number): Day {
  return {
    id: Math.random().toString(36).slice(2),
    label,
    remaining,
  };
}

function defaultSprint(): { totalPoints: number; days: Day[] } {
  return {
    totalPoints: 40,
    days: [
      newDay('Day 0', 40),
      newDay('Day 1', 38),
      newDay('Day 2', 35),
      newDay('Day 3', 32),
      newDay('Day 4', 30),
      newDay('Day 5', 27),
      newDay('Day 6', 22),
      newDay('Day 7', 18),
      newDay('Day 8', 14),
      newDay('Day 9', 8),
      newDay('Day 10', 0),
    ],
  };
}

export function BurndownChartGenerator() {
  const [totalPoints, setTotalPoints] = useState(40);
  const [days, setDays] = useState<Day[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { totalPoints: number; days: Day[] };
        if (parsed && Array.isArray(parsed.days) && parsed.days.length > 0) {
          setTotalPoints(parsed.totalPoints);
          setDays(parsed.days);
          setHydrated(true);
          return;
        }
      }
    } catch {}
    const d = defaultSprint();
    setTotalPoints(d.totalPoints);
    setDays(d.days);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ totalPoints, days }));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [totalPoints, days, hydrated]);

  function update(id: string, patch: Partial<Day>) {
    setDays((curr) => curr.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  }

  function remove(id: string) {
    setDays((curr) => curr.filter((d) => d.id !== id));
  }

  function add() {
    const last = days[days.length - 1];
    const next = last ? Math.max(0, last.remaining - 3) : totalPoints;
    const label = `Day ${days.length}`;
    setDays((curr) => [...curr, newDay(label, next)]);
  }

  function reset() {
    if (!confirm('Reset to default sprint?')) return;
    const d = defaultSprint();
    setTotalPoints(d.totalPoints);
    setDays(d.days);
  }

  function exportCsv() {
    const rows = [
      ['Day', 'Remaining', 'Ideal'],
      ...days.map((d, i) => [
        d.label,
        d.remaining,
        idealLine[i] !== undefined ? idealLine[i].toFixed(1) : '',
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'burndown.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const idealLine = useMemo(() => {
    if (days.length < 2) return [];
    const start = totalPoints;
    const end = 0;
    const step = (end - start) / (days.length - 1);
    return days.map((_, i) => start + step * i);
  }, [totalPoints, days]);

  const finalRemaining = days.length ? days[days.length - 1].remaining : 0;
  const completed = totalPoints - finalRemaining;
  const completionRate = totalPoints ? (completed / totalPoints) * 100 : 0;
  const onTrack = days.length > 1 ? days[days.length - 1].remaining <= idealLine[days.length - 1] : true;

  // Chart geometry
  const width = 720;
  const height = 320;
  const padding = { top: 24, right: 24, bottom: 40, left: 48 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const maxY = Math.max(totalPoints, ...days.map((d) => d.remaining));

  function xScale(i: number) {
    const denom = Math.max(1, days.length - 1);
    return padding.left + (i / denom) * innerW;
  }
  function yScale(v: number) {
    return padding.top + (1 - v / Math.max(1, maxY)) * innerH;
  }

  const idealPath = days.length
    ? days.map((_, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(idealLine[i] ?? 0)}`).join(' ')
    : '';
  const actualPath = days.length
    ? days.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.remaining)}`).join(' ')
    : '';

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading generator…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats panel */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total points" value={String(totalPoints)} />
        <StatCard label="Completed" value={`${completed} (${completionRate.toFixed(0)}%)`} accent />
        <StatCard label="Remaining" value={String(finalRemaining)} />
        <StatCard label="On track?" value={onTrack ? 'Yes' : 'Behind'} accent={onTrack} warn={!onTrack} />
      </div>

      {/* Chart */}
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-6">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-auto"
          role="img"
          aria-label="Sprint burndown chart"
        >
          {/* Y axis grid */}
          {[0, 0.25, 0.5, 0.75, 1].map((t) => (
            <g key={t}>
              <line
                x1={padding.left}
                y1={padding.top + t * innerH}
                x2={width - padding.right}
                y2={padding.top + t * innerH}
                stroke="currentColor"
                strokeOpacity="0.08"
              />
              <text
                x={padding.left - 8}
                y={padding.top + t * innerH}
                dy="0.35em"
                textAnchor="end"
                className="fill-muted-foreground text-[10px]"
              >
                {Math.round(maxY * (1 - t))}
              </text>
            </g>
          ))}
          {/* X axis labels */}
          {days.map((d, i) => (
            <text
              key={d.id}
              x={xScale(i)}
              y={height - padding.bottom + 18}
              textAnchor="middle"
              className="fill-muted-foreground text-[10px]"
            >
              {d.label.replace(/^Day\s+/, '')}
            </text>
          ))}
          {/* Ideal line */}
          {idealPath && (
            <path
              d={idealPath}
              fill="none"
              stroke="currentColor"
              strokeOpacity="0.3"
              strokeWidth="2"
              strokeDasharray="4 4"
            />
          )}
          {/* Actual line */}
          {actualPath && (
            <path d={actualPath} fill="none" stroke="hsl(var(--primary))" strokeWidth="2.5" />
          )}
          {/* Actual points */}
          {days.map((d, i) => (
            <circle
              key={d.id}
              cx={xScale(i)}
              cy={yScale(d.remaining)}
              r="4"
              fill="hsl(var(--primary))"
            />
          ))}
        </svg>
        <div className="flex justify-center gap-6 text-xs mt-2">
          <span className="flex items-center gap-2">
            <span className="inline-block w-4 h-0.5 bg-primary" /> Actual
          </span>
          <span className="flex items-center gap-2">
            <span
              className="inline-block w-4 h-0.5"
              style={{ borderTop: '2px dashed currentColor', opacity: 0.4 }}
            />{' '}
            Ideal
          </span>
        </div>
      </div>

      {/* Total points + day editor */}
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <label className="text-sm font-semibold">Total committed points</label>
          <input
            type="number"
            min={0}
            value={totalPoints}
            onChange={(e) => setTotalPoints(Math.max(0, Number(e.target.value)))}
            className="w-24 text-right border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/30">
                <th className="text-left py-2 px-3 font-semibold text-muted-foreground w-8">#</th>
                <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Day label</th>
                <th className="text-right py-2 px-3 font-semibold text-muted-foreground">
                  Remaining points
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {days.map((d, i) => (
                <tr key={d.id} className={i < days.length - 1 ? 'border-b border-border/40' : ''}>
                  <td className="py-2 px-3 text-muted-foreground font-mono text-xs">{i}</td>
                  <td className="py-2 px-3">
                    <input
                      type="text"
                      value={d.label}
                      onChange={(e) => update(d.id, { label: e.target.value })}
                      className="w-full bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="number"
                      min={0}
                      value={d.remaining}
                      onChange={(e) =>
                        update(d.id, { remaining: Math.max(0, Number(e.target.value)) })
                      }
                      className="w-24 text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                    />
                  </td>
                  <td className="py-2 px-2">
                    <button
                      onClick={() => remove(d.id)}
                      aria-label="Remove day"
                      className="text-muted-foreground hover:text-red-500 p-1 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {days.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-muted-foreground">
                    No data yet. Add a day to start.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <button
          onClick={add}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all"
        >
          <Plus className="w-4 h-4" />
          Add day
        </button>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={days.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center pt-2">
        Saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}

function StatCard({
  label,
  value,
  accent,
  warn,
}: {
  label: string;
  value: string;
  accent?: boolean;
  warn?: boolean;
}) {
  let cls = 'bg-white dark:bg-card border border-border/60';
  if (accent && !warn) cls = 'bg-primary/5 border border-primary/20';
  if (warn) cls = 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40';
  return (
    <div className={`rounded-2xl p-4 ${cls}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={`text-2xl font-bold mt-1 ${
          accent && !warn ? 'text-primary' : ''
        } ${warn ? 'text-amber-700 dark:text-amber-400' : ''}`}
      >
        {value}
      </div>
    </div>
  );
}
